import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Box, Typography, IconButton, TextField, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMqttConnection } from '../services/connectionContext/connectionContext';
import { v4 } from 'uuid';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: 4,
};

const closeButtonStyle = {
    position: 'absolute',
    right: 0,
};

const AddBrokerModal = () => {
    const { mqttConnect, mqttDisconnect, eventEmitter } = useMqttConnection();
    const componentId = useRef(v4());
    const timeoutRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('idle');

    const [brokerPort, setBrokerPort] = useState('');
    const [brokerHost, setBrokerHost] = useState('');
    const [portError, setPortError] = useState('');
    const [hostError, setHostError] = useState('');

    const [initialDataEventName, setInitialDataEventName] = useState(null);
    const [mqttStatusEventName, setMqttStatusEventName] = useState(null);
    const [broker, setBroker] = useState(null);

    const clearValues = useCallback(() => {
        setConnectionStatus('idle');
        setBrokerHost('');
        setBrokerPort('');
        setPortError('');
        setHostError('');
        setInitialDataEventName(null);
        setBroker(null);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        if (broker) {
            mqttDisconnect({ brokerUrl: broker, componentId: componentId.current });
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        clearValues();
    }, [broker, clearValues, mqttDisconnect, eventEmitter]);

    const handleOpen = useCallback(() => {
        setOpen(true);
        clearValues();
    }, [clearValues]);

    const handleInitialDataUpdate = useCallback(() => {
        // setUpdated(true);
        setConnectionStatus('updated');
        timeoutRef.current = setTimeout(() => {
            handleClose();
        }, 3000);
    }, [handleClose]);

    const handleBrokerStatusUpdate = useCallback((value) => {
        if (value !== 'connecting' && value !== 'connected') {
            // setConnectionFailed(true);
            setConnectionStatus('failed');
            timeoutRef.current = setTimeout(() => {
                handleClose();
            }, 3000);
        }
    }, [handleClose]);

    const handleHostChange = useCallback((value) => {
        setBrokerHost(value);
    }, []);

    const handlePortChange = useCallback((value) => {
        setBrokerPort(value);
    }, []);

    const handleAddBroker = useCallback(() => {
        if (brokerHost && brokerPort) {
            const brokerString = `mqtt://${brokerHost}:${brokerPort}`;
            const initialDataEventString = `INITIAL_DATA_UPDATE:${brokerString}`;
            const mqttStatusEventString = `MQTT_STATE:${brokerString}`;
            setBroker(brokerString);
            setInitialDataEventName(initialDataEventString);
            setMqttStatusEventName(mqttStatusEventString);
            // setConnecting(true);
            setConnectionStatus('connecting');
            mqttConnect({ brokerUrl: brokerString, automaticReconnect: false, componentId: componentId.current });
        }
    }, [brokerHost, brokerPort, mqttConnect]);

    useEffect(() => {
        if (!initialDataEventName) return;

        eventEmitter.on(initialDataEventName, handleInitialDataUpdate);

        return () => {
            eventEmitter.off(initialDataEventName, handleInitialDataUpdate);
        };
    }, [initialDataEventName, eventEmitter, handleInitialDataUpdate]);

    useEffect(() => {
        if (!mqttStatusEventName) return;

        eventEmitter.on(mqttStatusEventName, handleBrokerStatusUpdate);

        return () => {
            eventEmitter.off(mqttStatusEventName, handleBrokerStatusUpdate);
        };
    }, [mqttStatusEventName, eventEmitter, handleBrokerStatusUpdate]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    }, []);

    return (
        <div>
            <Button
                onClick={handleOpen}
                variant="contained"
                sx={{ backgroundColor: '#d9e4eb', color: 'black' }}
            >
                Add Broker
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={boxStyle}>
                    <IconButton sx={closeButtonStyle} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        Connect Broker
                    </Typography>
                    {connectionStatus !== 'idle' ? (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                            {connectionStatus === 'updated' ? (
                                <Typography variant="h6" sx={{ mt: 2 }}>Broker hinzugefügt</Typography>
                            ) : connectionStatus === 'failed' ? (
                                <Typography variant="h6" sx={{ mt: 2 }}>Keine Verbindung mit dem Broker möglich!</Typography>
                            ) : (
                                <>
                                    <CircularProgress />
                                    <Typography variant="h6" sx={{ mt: 2 }}>Verbindung wird hergestellt ...</Typography>
                                </>
                            )
                            }
                        </Box>
                    ) : (
                        <>
                            <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                Please enter the Host and Port to connect to the Broker.
                            </Typography>

                            <TextField
                                id="broker-host"
                                label="Host"
                                error={hostError.length > 0}
                                value={brokerHost}
                                onChange={(e) => handleHostChange(e.target.value)}
                                variant="standard"
                                helperText={hostError}
                            />

                            <TextField
                                id="broker-port"
                                label="Port"
                                type="number"
                                error={portError.length > 0}
                                value={brokerPort}
                                onChange={(e) => handlePortChange(e.target.value)}
                                variant="standard"
                                helperText={portError}
                            />
                            <Box sx={{ mt: 2 }}>
                                <Button onClick={handleAddBroker}>Add Broker</Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default AddBrokerModal;